import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Location", path: "/location" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-stone-100 ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="px-6 lg:px-14 flex items-center justify-between h-[76px]">
        {/* Logo — brightness-0 (black) on white bg; brightness-0 invert (white) on dark hero */}
        <Link to="/" className="flex-shrink-0 group">
          <img
            src="https://storage.readdy-site.link/project_files/e9818d6f-bba1-4e7d-b659-43a4cdb14da8/1ea3d842-56cd-4003-8ff3-01ce59ecb29a_GD-and-Assoc-logo-2.png?v=c43fa0a4468a2674f83bdbc3f0d450dd"
            alt="Glen Davis and Associates Photography — Bremerton WA"
            className="h-11 w-auto object-contain transition-opacity duration-400 group-hover:opacity-80 mix-blend-multiply"
            onError={(e) => {
              const el = e.currentTarget;
              el.style.display = "none";
              const sib = el.nextElementSibling as HTMLElement | null;
              if (sib) sib.style.display = "flex";
            }}
          />
          {/* Text fallback if image fails */}
          <span
            style={{ display: "none" }}
            className={`items-center gap-2 font-display font-bold text-lg leading-tight ${
              scrolled ? "text-charcoal" : "text-white"
            }`}
          >
            Glen Davis<span className="text-gold text-sm font-sans font-semibold ml-1">Photography</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[11px] font-sans font-semibold uppercase tracking-[0.18em] transition-colors duration-200 whitespace-nowrap relative group ${
                location.pathname === link.path
                  ? "text-gold"
                  : "text-stone-600 hover:text-charcoal"
              }`}
            >
              {link.label}
              <span className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-300 ${
                location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
              }`} />
            </Link>
          ))}
          <a
            href="tel:360-204-4747"
            className="ml-2 px-5 py-2.5 text-[11px] font-sans font-bold uppercase tracking-[0.14em] rounded-md transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 bg-charcoal text-white hover:bg-gold"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-phone-fill text-xs"></i>
            </span>
            Book Now — 360-204-4747
          </a>
        </nav>

        <button
          className="md:hidden p-2 transition-colors cursor-pointer rounded-md text-stone-800 hover:bg-stone-100"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl`}></i>
          </div>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 shadow-lg">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[11px] font-sans font-bold uppercase tracking-widest transition-colors cursor-pointer ${
                  location.pathname === link.path ? "text-gold" : "text-stone-700 hover:text-gold"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:360-204-4747"
              className="inline-flex items-center gap-2 px-5 py-3.5 bg-gold text-white text-[11px] font-bold uppercase tracking-widest rounded-md cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill"></i>
              </div>
              Book Now — 360-204-4747
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
